import type { LayerData } from '@/atoms/layers.atom';
import { getLinkSessionId } from '@/graphql/execute';
import { UserOrganizationRole } from '@/graphql/graphql';
import { useFeatureCheck } from '@/hooks/useFeature';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useFeatureFlagEnabled } from '@/lib/analytics';

import { analysisGate, getAnalysis, type AnalysisFlag } from './registry';
import type { AnalysisId, Availability } from './types';
import { useAnalysisProjectContext } from './useAnalysisProjectContext';

/**
 * One hook call; returns a resolver usable in loops (no hooks per item).
 *
 * Resolution order (analysis-launcher spec D6):
 *   1. PostHog flag off, not EDITOR, shared link (GIS-284), no project yet, or
 *      the context layer is not something this analysis can start from
 *      → hidden.
 *   2. Subscription gate not allowed → locked: visible, muted, and the click
 *      opens the upgrade dialog (never HTML `disabled`, which swallows it).
 *   3. Project prerequisite missing → visible, blocked with a reason.
 */
export function useAnalysisAvailability(): (id: AnalysisId, contextLayer?: LayerData) => Availability {
  const ctx = useAnalysisProjectContext();
  const { hasRole } = useOrganizations();
  const featureCheck = useFeatureCheck();
  // One top-level call per flag — hooks may not run in a loop. `!!` coerces
  // `undefined` (flag not loaded yet / no PostHog config) to off, matching the
  // old `!!useFeatureFlagEnabled(...)` pattern in Layers.tsx. The Record type
  // makes a flag added to ANALYSIS_FLAGS but not read here a compile error.
  const flags: Record<AnalysisFlag, boolean> = {
    deviationAnalysis: !!useFeatureFlagEnabled('deviationAnalysis'),
    surfaceDifference: !!useFeatureFlagEnabled('surfaceDifference'),
    stockpileAnalysis: !!useFeatureFlagEnabled('stockpileAnalysis'),
    rainfallAnalysis: !!useFeatureFlagEnabled('rainfallAnalysis'),
    groundAnalysis: !!useFeatureFlagEnabled('groundAnalysis'),
    meshExport: !!useFeatureFlagEnabled('meshExport'),
    crest: !!useFeatureFlagEnabled('crest'),
  };
  const isEditor = hasRole(UserOrganizationRole.Editor);
  const isSharedLink = !!getLinkSessionId();

  return (id, contextLayer) => {
    const descriptor = getAnalysis(id);
    // Context-dependent for stockpile (draw mode is the free tool) — `analysisGate`.
    const gateKey = analysisGate(id, contextLayer);
    const gate = gateKey === null ? { allowed: true, promptUpgrade: () => {} } : featureCheck(gateKey);
    const hidden: Availability = {
      visible: false,
      locked: false,
      blockedReasonKey: null,
      promptUpgrade: gate.promptUpgrade,
    };
    if (descriptor.flag !== null) {
      const needed = (Array.isArray(descriptor.flag) ? descriptor.flag : [descriptor.flag]) as AnalysisFlag[];
      if (!needed.some((f) => flags[f])) return hidden;
    }
    if (!isEditor || isSharedLink || ctx === null) return hidden;
    if (contextLayer && !descriptor.acceptsContext(contextLayer, ctx)) return hidden;
    if (!gate.allowed) {
      return { visible: true, locked: true, blockedReasonKey: null, promptUpgrade: gate.promptUpgrade };
    }
    return {
      visible: true,
      locked: false,
      blockedReasonKey: descriptor.prerequisite(ctx)?.reasonKey ?? null,
      promptUpgrade: gate.promptUpgrade,
    };
  };
}
